/**
 * "Report a problem": a message and the page it happened on, sent to POST /v1/support/report (it lands in the BO
 * issue sheet, Product Features & Support, and alerts Beatly). Open to everyone signed in.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { REPORT_SENT_TEXT } from './reportProblem';

export function ReportProblemDialog({ open, onOpenChange, page }: { open: boolean; onOpenChange: (open: boolean) => void; page: string }) {
  const [message, setMessage] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  useEffect(() => {
    if (open) { setMessage(''); setState('idle'); }
  }, [open]);

  const send = async () => {
    const text = message.trim();
    if (!text || state === 'sending') return;
    setState('sending');
    try {
      await coworkApi.launch.reportProblem({
        message: text,
        page,
        context: { title: typeof document !== 'undefined' ? document.title : undefined, viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : undefined },
      });
      setState('sent');
    } catch {
      setState('failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-problem-dialog">
        <DialogHeader>
          <DialogTitle>Report a problem</DialogTitle>
          <DialogDescription>Tell us what happened. We send the page you are on with it.</DialogDescription>
        </DialogHeader>
        {state === 'sent' ? (
          <p role="status" className="text-[14px] text-[#101828]">{REPORT_SENT_TEXT}</p>
        ) : (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); void send(); }}>
            <label className="block space-y-1.5">
              <span className="text-[13px] text-[#344054]" style={{ fontWeight: 500 }}>What happened?</span>
              <Textarea
                aria-label="What happened?"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={4000}
                rows={4}
                autoFocus
              />
            </label>
            <p className="text-[12px] text-[#667085]">Page: <span className="break-all text-[#475467]">{page || '/'}</span></p>
            {state === 'failed' ? <p role="alert" className="text-[13px] text-[#b42318]">We couldn't send it. Try again in a moment.</p> : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={!message.trim() || state === 'sending'} className="bg-[#BE43FF] text-white hover:bg-[#a83ae6]">
                {state === 'sending' ? 'Sending' : 'Send'}
              </Button>
            </DialogFooter>
          </form>
        )}
        {state === 'sent' ? (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
